'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import { useDensity } from '@liftango/ui-tokens';

export type IconButtonVariant = 'bordered' | 'ghost';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label: string;
  variant?: IconButtonVariant;
}

const Button = styled.button<{ $variant: IconButtonVariant; $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  border-radius: ${(p) => p.theme.radius.md};
  transition:
    background ${(p) => p.theme.transition.color},
    color ${(p) => p.theme.transition.color};

  ${(p) =>
    p.$variant === 'bordered'
      ? css`
          background: ${p.theme.colors.neutral.panel};
          border: 1px solid ${p.theme.colors.neutral.line};
          &:hover:not(:disabled) {
            background: ${p.theme.colors.neutral.panelAlt};
          }
        `
      : css`
          background: transparent;
          border: none;
          &:hover:not(:disabled) {
            background: ${p.theme.colors.neutral.line};
          }
        `}

  ${focusRing}
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'bordered', ...rest },
  ref,
) {
  const density = useDensity();
  const size = density.controlHeight;
  return (
    <Button ref={ref} $variant={variant} $size={size} aria-label={label} {...rest}>
      {icon}
    </Button>
  );
});
